import { memo, useCallback, useRef } from 'react'

import { Popover, PopoverActions } from '@mui/material'

import { Stack, Subheader3, TextWithLinks } from '@collaborate-health/uikit'
import { FormControlInfoContentType, FormControlResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

import { Container, Description, FormControlInfoImage, FormControlInfoVideo } from './FormControlInfoWindow.styles'

const getVideoPlayerSrc = (videoUrl: string) => {
  if (videoUrl.toLowerCase().endsWith('.ism/manifest')) {
    return [
      {
        src: videoUrl,
        type: 'application/vnd.ms-sstr+xml',
      },
      {
        src: videoUrl.replace('.ism/manifest', '_H264_2000kbps_AAC_und_ch2_96kbps.mp4'),
        type: 'video/mp4',
      },
    ]
  }

  return [
    {
      src: videoUrl,
      type: 'video/mp4',
    },
  ]
}

export interface FormControlInfoWindowProps {
  anchorEl: HTMLButtonElement | null
  open: boolean
  control: FormControlResponse
  onClose: () => void
}

const FormControlInfoWindow: React.FC<FormControlInfoWindowProps> = ({
  anchorEl,
  open,
  control: { Info: info, Label: label },
  onClose,
}) => {
  const actionRef = useRef<PopoverActions>(undefined!)

  const onReposition = useCallback(() => {
    actionRef.current?.updatePosition()
  }, [])

  if (!info) {
    return null
  }

  return (
    <Popover
      open={open}
      action={actionRef}
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      onClose={onClose}
      slotProps={{
        root: {
          onClick: (e) => {
            e.stopPropagation()
            e.preventDefault()
          },
        },
      }}
    >
      <Container spacing={1}>
        {info.Type === FormControlInfoContentType.Image && info.Url && (
          <FormControlInfoImage draggable={false} src={info.Url!} alt={info.FileName!} onLoad={onReposition} />
        )}

        {[FormControlInfoContentType.Video, FormControlInfoContentType.UnencodedVideo].includes(info.Type) &&
          (info.Url || info.EncodedUrl) && (
            <FormControlInfoVideo
              url={getVideoPlayerSrc(info.EncodedUrl ?? info.Url!)}
              controls
              height='auto'
              light={info.ThumbnailUrl ?? false}
              onReady={onReposition}
            />
          )}
        <Stack spacing={0.5}>
          <Subheader3 title={label!} noWrap>
            {label}
          </Subheader3>
          {info.Description && (
            <TextWithLinks>
              <Description>{info.Description}</Description>
            </TextWithLinks>
          )}
        </Stack>
      </Container>
    </Popover>
  )
}

export default memo(FormControlInfoWindow)

